<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

const store = useStore();
const route = useRoute();

const iframeRef = ref(null);
const iframeLoading = ref(true);

const appId = computed(() => Number(route.params.iframeAppId));

const iframeApp = computed(() => {
  const records = store.getters['iframeApps/getRecords'];
  return records.find(app => app.id === appId.value) || {};
});

const currentUser = computed(() => {
  const user = store.getters.getCurrentUser;
  if (!user) return {};
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    avatar_url: user.avatar_url,
    role: user.role,
  };
});

const currentAccount = computed(() => {
  const accountId = store.getters.getCurrentAccountId;
  return {
    id: accountId,
  };
});

const appContext = computed(() => ({
  currentAgent: currentUser.value,
  currentAccount: currentAccount.value,
}));

function sendContextToIframe() {
  if (!iframeRef.value?.contentWindow) return;
  const eventData = { event: 'appContext', data: appContext.value };
  iframeRef.value.contentWindow.postMessage(JSON.stringify(eventData), '*');
}

function onIframeLoad() {
  iframeLoading.value = false;
  sendContextToIframe();
}

function handleMessage(event) {
  if (event.data === 'chatwoot-iframe-app:fetch-info') {
    sendContextToIframe();
  }
}

onMounted(() => {
  store.dispatch('iframeApps/get');
  window.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
});

watch(appContext, () => {
  sendContextToIframe();
});
</script>

<template>
  <div class="flex flex-col h-full w-full overflow-hidden">
    <header
      v-if="iframeApp.name"
      class="flex items-center gap-2 px-4 py-2 border-b border-n-weak bg-n-background flex-shrink-0"
    >
      <i
        :class="iframeApp.icon || 'i-lucide-app-window'"
        class="size-4 text-n-slate-11"
      />
      <h2 class="text-sm font-medium text-n-slate-12 truncate">
        {{ iframeApp.name }}
      </h2>
      <span
        v-if="iframeApp.description"
        class="text-xs text-n-slate-11 truncate"
      >
        - {{ iframeApp.description }}
      </span>
    </header>
    <div class="relative flex-1 min-h-0">
      <div
        v-if="iframeLoading"
        class="absolute inset-0 flex items-center justify-center bg-n-background z-10"
      >
        <span class="text-n-slate-11 text-sm">Loading...</span>
      </div>
      <iframe
        v-if="iframeApp.url"
        ref="iframeRef"
        :src="iframeApp.url"
        class="w-full h-full border-0"
        allow="camera; microphone; clipboard-write; clipboard-read"
        @load="onIframeLoad"
      />
    </div>
  </div>
</template>
